import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import axios from 'axios';

import { HeaderV2, FooterV2 } from '../../components/layout';
import {
  HeroSearch,
  FeaturesSection,
  DestinationCategories,
  TestimonialsSection,
  CallToActionSection,
} from '../../components/sections';
import { ScrollReveal } from '../../components/animations';
import { colors } from '../../components/theme/colors';
import { DEMO_PACKAGES } from '../../data/demoPackages';
import { API_BASE_URL } from '../../config/api';

const HomePageV2 = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [featuredPackages, setFeaturedPackages] = useState([]);

  useEffect(() => {
    const fetchFeaturedPackages = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/packages?featured=true&limit=8`, {
          headers: { 'Content-Type': 'application/json' },
        });

        const apiPackages = response.data?.data;
        if (Array.isArray(apiPackages) && apiPackages.length > 0) {
          setFeaturedPackages(apiPackages.slice(0, 8));
        } else {
          setFeaturedPackages(DEMO_PACKAGES.filter((p) => p.isFeatured).slice(0, 8));
        }
      } catch (err) {
        console.error('Error fetching packages:', err);
        setFeaturedPackages(DEMO_PACKAGES.filter((p) => p.isFeatured).slice(0, 8));
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedPackages();
  }, []);

  return (
    <motion.div>
      <HeaderV2 />

      <Box component="main" sx={{ overflow: 'visible', bgcolor: 'background.default' }}>
        <HeroSearch
          onSearch={(data) =>
            navigate('/v2/flights', {
              state: data,
            })
          }
        />

        <DestinationCategories />

        <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#fff' }}>
          <Container maxWidth="lg">
            <ScrollReveal animation="fadeInUp">
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'end' }}>
                <Box sx={{ maxWidth: 480 }}>
                  <Typography variant="h2" sx={{ mb: 1 }}>
                    Featured trips
                  </Typography>
                  <Typography color="text.secondary">
                    Curated packages across Umrah, ziyarat, holidays, and local getaways.
                  </Typography>
                </Box>
                <Button variant="outlined" color="primary" onClick={() => navigate('/v2/packages')}>
                  View all trips
                </Button>
              </Box>
            </ScrollReveal>

            {loading ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                  gap: 2,
                }}
              >
                {[1, 2, 3].map((i) => (
                  <Box key={i} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <Skeleton variant="rectangular" width="100%" height={220} />
                    <Box sx={{ p: 2 }}>
                      <Skeleton variant="text" />
                      <Skeleton variant="text" width="70%" />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 5500, disableOnInteraction: false }}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
              >
                {featuredPackages.map((pkg) => (
                  <SwiperSlide key={pkg.id}>
                    <Box sx={{ pb: 5 }}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: '#fff',
                          height: '100%',
                          transition: 'border-color 0.2s ease',
                          '&:hover': { borderColor: colors.secondary.main },
                        }}
                      >
                        <Box sx={{ position: 'relative', height: 220 }}>
                          <Box
                            component="img"
                            src={
                              pkg.image ||
                              pkg.packageImage ||
                              'https://images.unsplash.com/photo-1488085061387-422e58bd4d86?auto=format&fit=crop&w=800&q=70'
                            }
                            alt={pkg.title || pkg.packageName || 'Trip'}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                          {(pkg.category || pkg.packageSubType) && (
                            <Typography
                              sx={{
                                position: 'absolute',
                                left: 12,
                                bottom: 12,
                                px: 1.25,
                                py: 0.35,
                                bgcolor: 'rgba(14,28,42,0.82)',
                                color: '#fff',
                                fontSize: '0.75rem',
                                borderRadius: 1,
                                fontWeight: 600,
                              }}
                            >
                              {pkg.category || pkg.packageSubType}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ p: 2.25 }}>
                          <Typography
                            sx={{
                              fontFamily: "'Fraunces', Georgia, serif",
                              fontWeight: 600,
                              fontSize: '1.2rem',
                              mb: 0.5,
                              lineHeight: 1.3,
                            }}
                          >
                            {pkg.title || pkg.packageName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                            {pkg.description || `${pkg.duration || pkg.nights || ''} days`}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.15rem' }}>
                              from {(
                                pkg.currency === 'PKR' ||
                                pkg.category === 'Pakistan' ||
                                pkg.category === 'Local' ||
                                pkg.tripScope === 'Domestic' ||
                                pkg.tripScope === 'Local' ||
                                /pakistan|swat|hunza|skardu|murree|lahore|naran/i.test((pkg.title || pkg.packageName || '') + ' ' + (pkg.destinationLabel || ''))
                              )
                                ? `PKR ${Number(pkg.price || pkg.basePrice || 0).toLocaleString()}`
                                : `$${Number(pkg.price || pkg.basePrice || 0).toLocaleString()}`}
                            </Typography>
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              onClick={() => navigate(`/v2/booking/${pkg.id}`)}
                            >
                              Book
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </Container>
        </Box>

        <FeaturesSection />
        <TestimonialsSection />
        <CallToActionSection onButtonClick={() => navigate('/v2/customized-booking')} />

        {/* Partner strip — marketplace teaser (MS16) */}
        <Box
          sx={{
            py: 6,
            background: colors.gradient.primary,
            color: '#fff',
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ maxWidth: 520 }}>
                <Typography
                  sx={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: { xs: '1.5rem', md: '1.85rem' },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Hotels & transport companies — partner with us
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.82)' }}>
                  Open an account, list your rooms, vehicles, and rates. Coming with our
                  affiliate marketplace.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.55)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
                href="#partner"
              >
                Become a partner
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      <FooterV2 />
    </motion.div>
  );
};

export default HomePageV2;
